import { Link } from "react-router-dom";
import { testimonials } from "@/mocks/testimonials";
import usePageSeo from "@/hooks/usePageSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TestimonialCard from "@/components/feature/TestimonialCard";
import SectionHeading from "@/components/feature/SectionHeading";

const ORIGIN = "https://ftosc.cn";

const philosophies = [
  {
    icon: "ri-shield-check-line",
    title: "不替你做判断",
    text: "我们整理事实、时间线和主要分歧，但不给结论。娱乐话题很少有标准答案，把判断权还给你。",
  },
  {
    icon: "ri-scales-3-line",
    title: "立场要一起出现",
    text: "如果只呈现一种声音，讨论就变成了通告。支持、质疑与中立的观点会被同时保留在同一页面。",
  },
  {
    icon: "ri-time-line",
    title: "跟着事件走",
    text: "话题有回转就更新，时间线会持续补充。旧的判断可以改，前提是你知道后来发生了什么。",
  },
  {
    icon: "ri-focus-3-line",
    title: "拒绝情绪流水线",
    text: "不放大会让人愤怒的细节，也不靠对立换阅读量。内容的节奏应该服务于理解，而不是刺激。",
  },
];

const principles = [
  "以公开可查的信息为整理基础，不采用来源不明的说法。",
  "涉及具体个人时保持克制，不做未经证实的指控与推断。",
  "观点讨论对事不对人，攻击发言者的内容不会被保留。",
  "商业内容会明确标注，不与话题整理内容混在一起。",
  "发现错误会及时更正，并在话题页标注修订时间。",
  "承认认知有限，欢迎来自不同立场的补充与反驳。",
];

export default function AboutPage() {
  usePageSeo({
    title: "关于我们 - 瓜田吃瓜内容社区介绍与内容原则",
    description:
      "瓜田吃瓜是一个以话题整理与观点讨论为核心的内容社区，坚持不替读者下判断、立场同时出现、跟着事件更新的内容原则，欢迎通过邮箱与我们联系。",
    keywords: "关于瓜田吃瓜,瓜田吃瓜介绍,内容原则,观点讨论社区",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于瓜田吃瓜",
        url: `${ORIGIN}/about`,
        inLanguage: "zh-CN",
        description: "介绍瓜田吃瓜的内容定位、发展理念、内容原则与联系方式。",
        mainEntity: {
          "@type": "Organization",
          name: "瓜田吃瓜",
          url: `${ORIGIN}/`,
          email: "hello@guatianchigua.cn",
          description:
            "以话题整理和观点讨论为核心的内容社区，覆盖综艺、影视、音乐、演出、明星动态与行业观察。",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${ORIGIN}/about` },
        ],
      },
    ],
  });

  return (
    <div className="w-full">
      <section className="w-full bg-background-200/50 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-primary-600 mb-3">
              <span className="w-6 h-px bg-primary-400" />
              About Us
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              关于瓜田吃瓜
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              我们不生产瓜，只是把散落在各处的瓜皮整理干净，再摆成一张能看清楚的地图。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="w-full h-64 sm:h-80 lg:h-[460px] rounded-lg overflow-hidden bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20illustration%20of%20an%20open%20notebook%20and%20overlapping%20speech%20bubbles%20in%20coral%20red%2C%20fresh%20green%20and%20cream%20tones%2C%20abstract%20geometric%20shapes%2C%20cozy%20community%20discussion%20theme%2C%20soft%20grain%20texture%2C%20no%20text%2C%20modern%20minimal%20poster%20style&width=1000&height=1200&seq=gty-about-01&orientation=portrait"
              alt="瓜田吃瓜内容社区办公与讨论主题插画"
              title="瓜田吃瓜 - 关于我们的内容理念"
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-snug text-balance">
              让围观这件事，稍微更有耐心一点
            </h2>
            <div className="mt-6 flex flex-col gap-4 text-sm md:text-base leading-relaxed text-foreground-700">
              <p>
                瓜田吃瓜是一个以泛娱乐话题为核心的内容社区。我们把综艺、影视、音乐、演出、明星动态和行业观察这几类讨论度最高的方向集中起来，用话题页的形式呈现事件的时间线、主要分歧点和讨论区里的不同立场。
              </p>
              <p>
                很多人对娱乐话题的印象是快、碎、情绪化。我们想做的恰恰相反：把一个话题放慢、铺开，让你在看完之后能理解大家为什么会吵起来，而不只是知道谁在吵。
              </p>
              <p>
                我们相信，同一个瓜本来就允许有不同的吃法。有人只想知道发生了什么，有人关心行业怎么运转，也有人单纯想找人聊聊自己的感受。这三种需求都值得被认真对待。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { value: "7", label: "内容分区" },
                { value: "1,200+", label: "已整理话题" },
                { value: "38万+", label: "观点讨论" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg bg-background-200/50 border border-background-200/70 p-4">
                  <div className="text-lg md:text-2xl font-bold text-primary-600">{item.value}</div>
                  <div className="mt-1 text-xs text-foreground-600">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-200/50 border-y border-background-200/70">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            eyebrow="Philosophy"
            title="我们坚持的四件事"
            description="不复杂，但每一条都会影响我们怎么选话题、怎么写内容、怎么处理讨论区。"
            align="center"
          />
          <div className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {philosophies.map((item) => (
              <article key={item.title} className="reveal rounded-lg bg-background-50 border border-background-200/80 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-700 text-xl">
                  <i className={item.icon} />
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="principles" className="w-full px-4 md:px-8 py-12 md:py-16 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          <div className="lg:col-span-1">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">内容原则</h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
              这些原则写在我们的编辑手册里，也是处理争议话题时的判断依据。
            </p>
          </div>
          <ul className="lg:col-span-2 flex flex-col gap-3">
            {principles.map((text) => (
              <li
                key={text}
                className="flex items-start gap-3 rounded-lg bg-background-50 border border-background-200/80 p-4 text-sm leading-relaxed text-foreground-700"
              >
                <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-sm">
                  <i className="ri-check-line" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="w-full bg-background-200/50 border-y border-background-200/70">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            eyebrow="Voices"
            title="用户怎么说"
            description="他们在这里花的时间，比任何介绍文字都更有说服力。"
            align="center"
          />
          <div className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {testimonials.slice(0, 3).map((item) => (
              <TestimonialCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="w-full px-4 md:px-8 py-12 md:py-16 scroll-mt-24">
        <div className="rounded-lg bg-secondary-100/80 border border-secondary-200 p-6 md:p-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-bold text-secondary-950">联系我们</h2>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-secondary-900/80">
                话题推荐、内容纠错、投稿合作，都可以直接发邮件。我们通常会在两个工作日内回复，涉及具体话题的纠错请附上可查证的来源。
              </p>
              <a
                href="mailto:hello@guatianchigua.cn"
                className="mt-5 inline-flex items-center gap-2 text-base font-medium text-primary-700 hover:text-primary-800 transition-colors"
              >
                <i className="ri-mail-send-line w-5 h-5 flex items-center justify-center" />
                hello@guatianchigua.cn
              </a>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 shrink-0">
              <Link
                to="/discussion"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-chat-smile-2-line w-4 h-4 flex items-center justify-center" />
                先去讨论区看看
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-800 text-sm border border-background-300 whitespace-nowrap hover:border-primary-300 transition-colors"
              >
                查看常见问题
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}